import React from 'react';

function emailLink() {
  return <a href="mailto:team@sevenloves.co.uk">team@sevenloves.co.uk</a>;
}

function externalLink(href, label) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
}

const en = {
  languageSwitcher: {
    label: 'Language',
    options: {
      en: 'EN',
      ja: 'JA',
      ko: 'KO',
      ms: 'MS',
      th: 'TH',
      tr: 'TR',
    },
    names: {
      en: 'English',
      ja: 'Japanese',
      ko: 'Korean',
      ms: 'Malay',
      th: 'Thai',
      tr: 'Turkish',
    },
  },
  nav: {
    home: 'Home',
    privacy: 'Privacy',
    terms: 'Terms',
    smsOptIn: 'SMS Opt In',
    guidelines: 'Guidelines',
    safety: 'Safety',
    deleteAccount: 'Delete Account',
    support: 'Support',
    download: 'Download',
    brandAriaLabel: '7Loves Home',
    menuAriaLabel: 'Open menu',
  },
  store: {
    groupAriaLabel: 'Store links',
    appStoreAriaLabel: 'Download on the App Store',
    appStoreOverline: 'Download on the',
    appStoreTitle: 'App Store',
    googlePlayAriaLabel: 'Get it on Google Play',
    googlePlayOverline: 'Get it on',
    googlePlayTitle: 'Google Play',
  },
  home: {
    metaTitle: 'Home',
    title: 'Welcome to 7Loves',
    subtitle: 'Blind Dating - Match First. Reveal More.',
    intro:
      'Seven Loves is a modern dating app focused on meaningful connections. Unlike traditional swipe-based apps, Seven Loves introduces a unique blind-match experience where users connect first and reveal profiles gradually.',
    howItWorks: 'How It Works',
    details:
      'Users are matched based on compatibility, start conversations, and unlock profile details over time. This approach encourages genuine connections instead of superficial swiping.',
    primaryCta: 'Download',
    secondaryCta: 'Contact Us',
  },
  downloadPage: {
    metaTitle: 'Download',
    title: 'Get 7Loves',
    subtitlePrefix: 'You can continue to',
    subtitleSuffix: '.',
    fallback: 'You can also use the store links below at any time.',
    modalTitle: 'Open store?',
    modalBodyPrefix: 'We can try to open',
    modalBodySuffix: 'on this device.',
    primaryCta: 'Open Store',
    secondaryCta: 'Not Now',
    backHome: 'Back Home',
    destinations: {
      ios: 'the App Store',
      android: 'Google Play',
    },
  },
  smsOptIn: {
    metaTitle: 'SMS Verification Opt-In',
    title: 'SMS Verification Opt-In',
    subtitle:
      'To receive a one-time verification code, please confirm you are 18+ and enter your mobile number.',
    dobLabel: 'Date of Birth (DD/MM/YYYY)',
    dayPlaceholder: 'DD',
    monthPlaceholder: 'MM',
    yearPlaceholder: 'YYYY',
    phoneLabel: 'Mobile Number',
    phonePlaceholder: '+1 555 000 0000',
    sendCode: 'Send Verification Code',
    disclosure:
      'By tapping "Send Verification Code", you confirm you are 18 or older and consent to receive one-time SMS verification codes from Seven Loves for authentication purposes. Message and data rates may apply. No marketing messages will be sent.',
    privacyLink: 'Privacy Policy',
  },
  pages: {
    privacy: {
      metaTitle: 'Privacy Policy',
      title: '7Loves Privacy Policy',
      subtitle: (
        <>
          Your privacy, our responsibility.
          <br />
          This page explains how your personal data is processed and protected by Seven Loves
          Limited ("7Loves").
        </>
      ),
      sections: [
        {
          title: '1. Introduction',
          blocks: [
            {
              type: 'paragraph',
              content: (
                <>
                  Welcome to the 7Loves Privacy Policy.
                  <br />
                  We are committed to protecting your data and using it only for clear, legitimate
                  purposes. Please read this policy carefully for a detailed explanation of how
                  your data is processed, stored, and protected on 7Loves.
                </>
              ),
            },
          ],
        },
        {
          title: '2. Data Controller',
          blocks: [
            {
              type: 'paragraph',
              content: (
                <>
                  Your data is processed by Seven Loves Limited, located at Richmond Office,
                  United Kingdom. You can contact us for any requests or questions regarding your
                  personal data at <b>team@sevenloves.co.uk</b>.
                </>
              ),
            },
          ],
        },
        {
          title: '3. Categories of Personal Data Collected',
          blocks: [
            {
              type: 'paragraph',
              content: 'The following categories of personal data are processed by 7Loves:',
            },
            {
              type: 'list',
              items: [
                <>
                  <b>Identity Information:</b> Name, surname, date of birth, height
                </>,
                <>
                  <b>Contact Information:</b> Apple ID, phone number
                </>,
                <>
                  <b>Visual Data:</b> Profile photos (processed and blurred in 6 segments)
                </>,
                <>
                  <b>Relationship and Family:</b> Information about children and relationship
                  preferences
                </>,
                <>
                  <b>Messages:</b> All user-to-user messages (archived)
                </>,
                <>
                  <b>Location:</b> Device geolocation if permission is granted
                </>,
                <>
                  <b>Usage and Technical Data:</b> Device info, IP address, application activity
                  and interactions
                </>,
              ],
            },
          ],
        },
        {
          title: '4. Legal Basis and Purposes of Data Processing',
          blocks: [
            {
              type: 'list',
              items: [
                'To create and manage your account and user profile',
                'To match you with other users and facilitate communication',
                'To provide and maintain app features and services',
                'To analyze usage and improve service quality',
                'To ensure security, fraud prevention, and compliance with legal obligations',
                'To develop, test, and improve AI and machine learning systems for better matching and safety',
              ],
            },
            {
              type: 'paragraph',
              content:
                'We process your data on the basis of your explicit consent, for performance of the service agreement, and to comply with our legal obligations.',
            },
          ],
        },
        {
          title: '5. Data Sharing and Transfers',
          blocks: [
            {
              type: 'list',
              items: [
                'Some of your data is visible to other users (such as profile photos and match data).',
                'Data may be shared with trusted service providers (cloud hosting, moderation, payment processing) for service delivery and security.',
                'When required by law, data may be shared with competent public authorities.',
                'No data is transferred outside the UK/EU without adequate safeguards.',
                'Automated moderation tools (for example image/AI moderation) may be used, sometimes with third-party providers, for your safety and platform integrity.',
              ],
            },
          ],
        },
        {
          title: '6. Data Retention Periods',
          blocks: [
            {
              type: 'list',
              items: [
                'All user data is deleted or anonymized when no longer needed for the purposes stated above, or when you delete your account, unless legal obligations require longer storage.',
                <>
                  Data is retained for <b>3 months after account closure</b> and <b>1 year after an
                  account is banned</b>.
                </>,
                <>
                  Records of your consents (for example accepting terms or privacy policy) are kept
                  for <b>5 years</b> to comply with legal obligations.
                </>,
                <>
                  Customer service communications are kept for <b>6 years</b> after last
                  communication for dispute resolution and legal compliance.
                </>,
                <>
                  Profile and matching data for potential legal claims is retained for <b>1 year</b>{' '}
                  after deletion.
                </>,
                <>
                  For banned users, minimal data may be kept for up to <b>10 years</b> to protect
                  the safety of the user community.
                </>,
                'If there is a pending dispute or legal claim, data may be retained until the dispute is resolved.',
              ],
            },
          ],
        },
        {
          title: "7. Children's Privacy",
          blocks: [
            {
              type: 'paragraph',
              content: (
                <>
                  7Loves is strictly intended for users aged 18 and above.
                  <br />
                  Users under 18 are not permitted to use the service. Any account found to belong
                  to a minor will be deleted. Please report underage users through our in-app
                  tools.
                </>
              ),
            },
          ],
        },
        {
          title: '8. Security and Automated Filtering',
          blocks: [
            {
              type: 'list',
              items: [
                'Automated filters and AI moderation tools are used to detect harmful, illegal, or inappropriate content and behavior.',
                'Any violation may result in account suspension, content removal, or notification to authorities.',
                'User reports and complaints are taken seriously and processed by both automated and human review.',
              ],
            },
          ],
        },
        {
          title: '9. Profiling and Automated Decision-Making',
          blocks: [
            {
              type: 'list',
              items: [
                'Automated matching algorithms and AI are used to suggest relevant profiles and enhance safety.',
                'No sensitive characteristics (race, religion, and similar traits) are used in matchmaking or automated decisions.',
                'Important decisions (like account bans) can always be reviewed by a human upon request.',
              ],
            },
          ],
        },
        {
          title: '10. Your Rights',
          blocks: [
            {
              type: 'list',
              items: [
                <>
                  <b>Access:</b> You can request information about your processed data at any time.
                </>,
                <>
                  <b>Rectification:</b> You can request the correction of incorrect or incomplete
                  data.
                </>,
                <>
                  <b>Erasure ("Right to be Forgotten"):</b> You can request deletion of your data,
                  under certain conditions.
                </>,
                <>
                  <b>Restriction:</b> You can request the limitation of data processing in certain
                  cases.
                </>,
                <>
                  <b>Notification:</b> You have the right to be notified about corrections or
                  deletions.
                </>,
                <>
                  <b>Portability:</b> You can request your data in a portable format or request its
                  transfer to another service provider.
                </>,
                <>
                  <b>Objection:</b> You can object to processing for direct marketing or other
                  legitimate interests.
                </>,
                <>
                  <b>Automated decisions:</b> You have the right to contest decisions made solely
                  by automated means.
                </>,
              ],
            },
            {
              type: 'paragraph',
              content: (
                <>
                  For all data rights requests, please email <b>team@sevenloves.co.uk</b>.
                  <br />
                  If you are unsatisfied, you can lodge a complaint with the UK Information
                  Commissioner's Office (ICO).
                </>
              ),
            },
          ],
        },
        {
          title: '11. Contact and Complaints',
          blocks: [
            {
              type: 'paragraph',
              content: (
                <>
                  If you have any questions about this policy or your personal data, you can
                  contact us at <b>team@sevenloves.co.uk</b> or by mail at [ADDRESS], United
                  Kingdom.
                  <br />
                  You also have the right to file a complaint with the Information Commissioner's
                  Office ({externalLink('https://ico.org.uk/', 'ico.org.uk')}).
                </>
              ),
            },
          ],
        },
        {
          title: '12. Updates to This Privacy Policy',
          blocks: [
            {
              type: 'paragraph',
              content: (
                <>
                  This policy may be updated from time to time. You will be notified of major
                  changes through the app or by email. The latest version will always be available
                  at <b>https://sevenloves.com/privacy</b>.
                </>
              ),
            },
          ],
        },
      ],
    },
    terms: {
      metaTitle: 'Terms of Service',
      title: '7Loves Terms of Service',
      subtitle:
        'Please read these terms carefully before using 7Loves. By accessing or using our app/website, you agree to these Terms of Service.',
      sections: [
        {
          title: '1. Introduction',
          blocks: [
            {
              type: 'paragraph',
              content: (
                <>
                  Welcome to the 7Loves Terms of Service ("Terms" or "Agreement"). This Agreement
                  is a binding contract between you and <b>Seven Loves Limited</b> ("7Loves").
                  Before using the 7Loves app or website ("7Loves" or "App"), please review these
                  Terms, as well as our Privacy Policy, Cookie Policy, and Community Guidelines. By
                  accessing, viewing, or using 7Loves, you agree to be bound by these Terms and our
                  policies and procedures. If you do not agree, you must discontinue use of our
                  services.
                </>
              ),
            },
            {
              type: 'paragraph',
              content: (
                <>
                  7Loves services are provided by <b>Seven Loves Limited</b> at <b>Richmond
                  Office</b>. We may update or change these Terms at any time, and such changes
                  will be posted on this page with the updated effective date. In certain cases, we
                  may notify you by email. Continued use of the service after changes means you
                  accept the updated Terms. If you do not accept the changes, you must stop using
                  the service immediately.
                </>
              ),
            },
          ],
        },
        {
          title: '2. Eligibility and User Commitments',
          blocks: [
            {
              type: 'list',
              items: [
                'You must be at least 18 years old.',
                'You have the legal capacity to enter into this contract and are not prohibited from using the service by law.',
                'You may only maintain one account on 7Loves.',
                'You have not been convicted of a serious, violent, or sexual offense, unless pardoned for a non-violent crime and not posing a threat to others.',
              ],
            },
            {
              type: 'paragraph',
              content:
                'If you do not meet these criteria, or if you cease to meet them, your right to access 7Loves is automatically revoked and your account must be deleted. We reserve the right to remove access at any time without notice.',
            },
            {
              type: 'paragraph',
              content: 'You also agree to:',
            },
            {
              type: 'list',
              items: [
                'Comply with all applicable laws and regulations, including privacy, intellectual property, anti-spam, and others.',
                'Use only the latest version of the app/website.',
                'Regularly review and comply with our Community Guidelines.',
                'Take all necessary steps to keep your login details confidential.',
              ],
            },
          ],
        },
        {
          title: '3. Usage Restrictions',
          blocks: [
            {
              type: 'paragraph',
              content: (
                <>
                  While using 7Loves, you agree <b>not</b> to:
                </>
              ),
            },
            {
              type: 'list',
              items: [
                'Provide misleading, false, or fake information about your identity, age, or relationships.',
                'Use the service in a way that disrupts or harms the platform, servers, or other users.',
                'Use the service for unlawful, malicious, or harmful purposes, including money laundering or financial crimes.',
                'Engage in harassment, threats, stalking, defamation, or abusive behavior.',
                'Share, transmit, or distribute content prohibited by law.',
                'Request passwords or personal info from other users, or disclose others’ info without consent.',
                'Request or solicit money or valuables from users.',
                'Use another user’s account or allow others to use yours.',
                'Engage in scams, pyramid schemes, or illegal revenue models.',
                'Use the service for political campaign financing or influencing elections, except for sharing personal political views.',
                'Disclose confidential or proprietary information you have no right to share.',
                'Copy, modify, distribute, or create derivative works of member or 7Loves content without explicit permission.',
                'Imply or state you are endorsed by 7Loves unless officially authorized.',
                'Use automated tools (bots, scrapers, and similar methods) to access or interact with the platform, or attempt to bypass our systems.',
                'Upload viruses, malware, or harmful code.',
                'Impersonate headers or identifiers to hide the source of information.',
                'Use meta tags, codes, or devices referencing 7Loves for redirecting users elsewhere.',
                "Reverse engineer, decrypt, or attempt to access or publish 7Loves' source code or interfaces without permission.",
                'Use AI or third-party apps to interact with our service or content without written consent.',
                'Probe, scan, or test the vulnerability of our systems or networks.',
                'Encourage or support violations of these Terms.',
                'Create a new account without permission after suspension or termination.',
                'Abuse or misuse our complaint/report systems.',
              ],
            },
            {
              type: 'paragraph',
              content: 'Any violation may result in immediate loss of access to 7Loves.',
            },
          ],
        },
        {
          title: '4. Content Types and Responsibilities',
          blocks: [
            {
              type: 'list',
              items: [
                <>
                  <b>Your Content:</b> Content you upload or provide via your profile (must comply
                  with Community Guidelines and these Terms).
                </>,
                <>
                  <b>Member Content:</b> Content provided by other users (7Loves does not guarantee
                  its accuracy).
                </>,
                <>
                  <b>Our Content:</b> Content provided by 7Loves (protected by copyright and
                  trademark law).
                </>,
              ],
            },
            {
              type: 'subheading',
              content: 'Prohibited Content Includes (but is not limited to):',
            },
            {
              type: 'list',
              items: [
                'Offensive, abusive, or psychologically distressing content.',
                'Obscene, pornographic, violent, or nude content.',
                'Threatening, discriminatory, hateful, or fanatical content.',
                'Content promoting illegal activity, terrorism, hate, or criminal offenses.',
                'Content encouraging self-harm, eating disorders, dangerous challenges, or violent extremism.',
                'Defamatory, humiliating, or false information.',
                'Commercial activities, promotions, advertising, or requests for sex work or "sugar dating."',
                'Spam or facilitation of spam.',
                'Viruses, malware, spyware, worms, or any code intended to harm systems or steal data.',
                'Content violating third-party rights.',
                'Unauthorized images of others, especially minors.',
                'Any content not suited for the platform’s intended use or that damages the reputation of 7Loves or affiliates.',
              ],
            },
            {
              type: 'paragraph',
              content:
                'You are responsible for your content and may be liable for any claims arising from it. Sharing personal/bank/payment info in your profile is at your own risk. Content uploaded to 7Loves may be visible to others, and you grant 7Loves a worldwide, royalty-free, non-exclusive, perpetual license to use, edit, copy, adapt, or remove your content as needed.',
            },
          ],
        },
        {
          title: '5. Reporting and Moderation',
          blocks: [
            {
              type: 'paragraph',
              content:
                'Inappropriate or prohibited content or behavior may result in account suspension or termination. You may report content or users via our in-app "Report" features or by contacting us. We may remove content or take other action at our sole discretion, and may cooperate with law enforcement as required.',
            },
          ],
        },
        {
          title: '6. Privacy',
          blocks: [
            {
              type: 'paragraph',
              content:
                'For information on how we collect, use, and share your personal data, please review our Privacy Policy. By using our service, you consent to data processing in accordance with our Privacy Policy.',
            },
          ],
        },
        {
          title: '7. Account Suspension or Termination',
          blocks: [
            {
              type: 'paragraph',
              content:
                'Violation of our Terms or Community Guidelines, or any behavior we consider inappropriate or unlawful, may result in suspension or termination of your account. You may also delete your account at any time through the app’s "Delete Account" feature.',
            },
            {
              type: 'paragraph',
              content:
                '7Loves reserves the right to restrict access, terminate accounts, or use technical, legal, or other measures without prior notice or liability. Termination does not entitle you to any refund, and your data will be retained or deleted according to our Privacy Policy.',
            },
          ],
        },
        {
          title: '8. Disclaimers and Limitations of Liability',
          blocks: [
            {
              type: 'paragraph',
              content:
                'The app, website, our content, and member content are provided "as is" and "as available," without warranties of any kind. 7Loves does not guarantee that the service will meet your expectations, be error-free, secure, or uninterrupted, or that matches will be reliable or accurate. Use of the service is at your own risk, and you are solely responsible for any interactions or communications with other users. We are not responsible for user conduct, accuracy of member content, or any losses, damages, or disputes arising from your use of the platform.',
            },
            {
              type: 'paragraph',
              content:
                'Links to third-party websites or content are provided for convenience; 7Loves does not endorse or control these sites and is not responsible for their content, privacy, or terms. Accessing third-party sites is at your own risk.',
            },
            {
              type: 'paragraph',
              content:
                'If any implied warranties or responsibilities cannot be excluded under applicable law, our liability will be limited to the minimum extent required by law.',
            },
          ],
        },
        {
          title: '9. Dispute Resolution',
          blocks: [
            {
              type: 'paragraph',
              content:
                'These Terms are governed by the laws of England and Wales. All disputes will first be attempted to be resolved amicably. If resolution is not possible, either party may seek relief in the courts of England.',
            },
          ],
        },
        {
          title: '10. Final Provisions',
          blocks: [
            {
              type: 'paragraph',
              content:
                'These Terms constitute the entire agreement between you and 7Loves regarding use of the service, and supersede all previous agreements or representations (except for the Privacy Policy). If any provision is found unenforceable, the remaining provisions remain in effect.',
            },
            {
              type: 'paragraph',
              content:
                '7Loves may update or modify these Terms at any time. Changes will be posted on this page and may be notified to you via email or within the app. It is your responsibility to review the Terms regularly. Continued use after changes constitutes acceptance of the updated Terms.',
            },
            {
              type: 'paragraph',
              content:
                'By registering for 7Loves, you acknowledge and accept all the terms of this agreement. These Terms take effect upon your registration and create a binding legal relationship between you and 7Loves.',
            },
          ],
        },
      ],
    },
    communityGuidelines: {
      metaTitle: 'Community Guidelines',
      title: '7Loves Community Guidelines',
      subtitle: 'Our rules for a respectful, safe, and positive dating experience.',
      sections: [
        {
          title: 'Welcome to the 7Loves Community!',
          blocks: [
            {
              type: 'paragraph',
              content:
                'These guidelines are designed to keep 7Loves a safe, sincere, and positive environment for everyone. By using 7Loves, you agree to follow these rules at all times, whether interacting with your matches, other users, or our team.',
            },
          ],
        },
        {
          title: '1. Adults Only - Real People, Real Intentions',
          blocks: [
            {
              type: 'paragraph',
              content:
                '7Loves is for people aged 18 and older. You must use your real identity and provide accurate information. Using someone else’s identity, hiding your age, or providing false information is strictly prohibited.',
            },
          ],
        },
        {
          title: '2. Be Yourself',
          blocks: [
            {
              type: 'list',
              items: [
                'Your profile photos and descriptions must represent you honestly. Do not use edited, heavily filtered, or misleading images.',
                'All photos must clearly show your face and be appropriate for a dating environment. No group photos as the main profile picture.',
                'No sexual, pornographic, violent, or disturbing images are allowed.',
              ],
            },
          ],
        },
        {
          title: '3. Respect and Empathy',
          blocks: [
            {
              type: 'list',
              items: [
                'Every user deserves respect. Harassment, bullying, intimidation, discrimination, or hate speech will not be tolerated.',
                'Do not use language or images that target people based on race, religion, nationality, gender, sexual orientation, age, or disability.',
                'No threats, abusive language, or repeated unwanted contact.',
              ],
            },
          ],
        },
        {
          title: '4. Meaningful and Respectful Communication',
          blocks: [
            {
              type: 'list',
              items: [
                'All messages and interactions should be respectful and consensual. Do not send sexual or harassing messages, even as jokes.',
                'No spam, repetitive, or irrelevant messages.',
                'If someone is not interested or asks you to stop, you must respect their wishes.',
              ],
            },
          ],
        },
        {
          title: '5. No Requests for Sensitive Information',
          blocks: [
            {
              type: 'list',
              items: [
                'Do not request or share private information such as phone numbers, addresses, bank or credit card details, or other sensitive information through the app.',
                'Never share your password or allow others to use your account.',
                'Do not ask for or send money or other valuables.',
              ],
            },
          ],
        },
        {
          title: '6. Zero Tolerance for Violence and Exploitation',
          blocks: [
            {
              type: 'list',
              items: [
                'No content or behavior that promotes, glorifies, or threatens violence, exploitation, abuse, or illegal activity.',
                'Any child sexualization, abuse, or the sharing of child imagery (real or fictional) is strictly forbidden and will be reported to authorities.',
                'Threats, doxxing, or attempts to incite harm are grounds for immediate account removal.',
              ],
            },
          ],
        },
        {
          title: '7. No Commercial Activity or Solicitation',
          blocks: [
            {
              type: 'list',
              items: [
                '7Loves is not for business promotion, sales, advertising, recruiting, multi-level marketing, or any kind of financial solicitation.',
                'No "sugar dating", escort services, or requests for transactional relationships.',
              ],
            },
          ],
        },
        {
          title: '8. Spam, Scams, and Misuse',
          blocks: [
            {
              type: 'list',
              items: [
                'Do not send or promote spam, scams, fake giveaways, pyramid schemes, or fraudulent links.',
                'No impersonation of other users, brands, or public figures.',
                'Do not use bots, scripts, or unauthorized third-party apps to interact with 7Loves.',
              ],
            },
          ],
        },
        {
          title: '9. Reporting and Safety',
          blocks: [
            {
              type: 'list',
              items: [
                'If you see suspicious, abusive, or harmful behavior, report it to us immediately through the in-app reporting tool or by contacting support.',
                'All reports are taken seriously and treated confidentially.',
                'False reports made maliciously are also a violation of these guidelines.',
              ],
            },
          ],
        },
        {
          title: '10. Consequences of Violations',
          blocks: [
            {
              type: 'list',
              items: [
                'Violations of these guidelines may result in content removal, warnings, temporary or permanent suspension of your account, or legal action if necessary.',
                'Severe violations (such as child safety breaches, threats, or harassment) result in immediate, permanent ban and may be reported to law enforcement.',
              ],
            },
          ],
        },
        {
          title: "11. Community Safety Is Everyone's Responsibility",
          blocks: [
            {
              type: 'paragraph',
              content:
                'By being part of 7Loves, you agree to help us build a sincere, safe, and welcoming community for everyone. Treat others as you wish to be treated. Let’s make this a positive experience together.',
            },
          ],
        },
        {
          title: '12. Child Safety',
          blocks: [
            {
              type: 'list',
              items: [
                'Seven Loves has zero tolerance for child sexual abuse and exploitation (CSAE).',
                'Users must be 18 years or older.',
                'Any content involving minors is strictly prohibited.',
                'We actively remove and report such content.',
                'Users can report inappropriate content in-app.',
                <>Contact: {emailLink()}</>,
              ],
            },
          ],
        },
        {
          title: '13. Updates and Contact',
          blocks: [
            {
              type: 'paragraph',
              content: (
                <>
                  7Loves reserves the right to update these guidelines at any time. Please review
                  them regularly for changes. If you have questions or need support, contact us at{' '}
                  <b>team@sevenloves.co.uk</b>.
                </>
              ),
            },
          ],
        },
      ],
    },
    dateSafety: {
      metaTitle: 'Date Safety',
      title: 'Stay Safe While Dating',
      subtitle:
        'Your safety is our top priority. Here are our tips and rules for safe, respectful connections on 7Loves.',
      sections: [
        {
          title: 'Protect Your Privacy',
          blocks: [
            {
              type: 'list',
              items: [
                "Don't share your home address, workplace, or private contact details (like phone number or email) too early.",
                'Never send money or financial details to anyone you meet on 7Loves.',
                "Be cautious about sharing personal photos, especially if you haven't met in person.",
              ],
            },
          ],
        },
        {
          title: 'Meeting in Person',
          blocks: [
            {
              type: 'list',
              items: [
                'Always meet for the first time in a public place (like a cafe, restaurant, or busy park).',
                "Tell a friend or family member your plans: where you're going, who you're meeting, and when you expect to return.",
                'Arrange your own transportation and keep your phone charged and with you at all times.',
                "Don't feel pressured to do anything you're uncomfortable with. Trust your instincts and leave if something feels off.",
              ],
            },
          ],
        },
        {
          title: 'Online Interactions',
          blocks: [
            {
              type: 'list',
              items: [
                'If someone asks you for money, reports a crisis, or tries to get you off the app quickly, be careful. These may be signs of scams.',
                'Be wary of users who refuse to meet in person or talk on a video call.',
                "Watch out for fake profiles and people using someone else's photos. If something feels suspicious, report it to us.",
              ],
            },
          ],
        },
        {
          title: 'Reporting and Blocking',
          blocks: [
            {
              type: 'list',
              items: [
                'Use our in-app reporting and blocking features for any behavior that makes you feel unsafe, uncomfortable, or disrespected.',
                'We review all reports and take swift action to protect our community.',
                'If you ever feel threatened or in immediate danger, contact your local authorities right away.',
              ],
            },
          ],
        },
        {
          title: 'Looking Out for Each Other',
          blocks: [
            {
              type: 'list',
              items: [
                'If you see suspicious, fake, or abusive accounts, report them. Help us keep 7Loves safe for everyone.',
                'If you learn someone is under 18, report them. Only adults are allowed on 7Loves.',
                "Let's create a community where everyone feels safe, respected, and supported.",
              ],
            },
          ],
        },
        {
          title: 'Additional Resources',
          blocks: [
            {
              type: 'paragraph',
              content: 'For more tips and support, check out these resources:',
            },
            {
              type: 'list',
              items: [
                externalLink(
                  'https://www.met.police.uk/advice/advice-and-information/sa/sexual-assault/keeping-safe-on-a-date/',
                  'Met Police: Keeping Safe on a Date'
                ),
                externalLink(
                  'https://www.onlinedatingassociation.org.uk/safety/',
                  'Online Dating Association: Safety Centre'
                ),
                externalLink('https://www.getsafeonline.org/', 'Get Safe Online'),
              ],
            },
          ],
        },
        {
          title: 'Contact Us',
          blocks: [
            {
              type: 'paragraph',
              content: (
                <>
                  If you ever feel unsafe, need help, or have any concerns, please contact our
                  team at <b>team@sevenloves.co.uk</b>.
                </>
              ),
            },
          ],
        },
      ],
    },
    deleteAccount: {
      metaTitle: 'Delete Account',
      title: 'Delete Your Account - Seven Loves: Blind Date Dating App',
      subtitle:
        'Seven Loves: Blind Date Dating App is developed and operated by Seven Loves Limited.',
      blocks: [
        {
          type: 'paragraph',
          content:
            'Seven Loves is a dating application that allows users to connect and match with others.',
        },
        {
          type: 'paragraph',
          content:
            'If you would like to permanently delete your account and associated data from Seven Loves, you can request account deletion using the process below.',
        },
      ],
      sections: [
        {
          title: 'How to delete your account',
          blocks: [
            {
              type: 'list',
              items: [
                <>Send an email to {emailLink()}</>,
                'Include the email address or phone number associated with your account',
                'Our team will verify your request',
              ],
            },
          ],
        },
      ],
      footerBlocks: [
        {
          type: 'paragraph',
          content:
            'Once your request is confirmed, your account and all associated data will be permanently deleted within 7 days.',
        },
        {
          type: 'paragraph',
          content: 'Users can also delete their account directly within the app.',
        },
        {
          type: 'paragraph',
          content: (
            <>
              If you need further assistance, contact us at {emailLink()}.
            </>
          ),
        },
        {
          type: 'paragraph',
          content: (
            <>
              Seven Loves Limited
              <br />
              {externalLink('https://sevenloves.co.uk', 'https://sevenloves.co.uk')}
            </>
          ),
        },
      ],
    },
  },
};

export default en;
